import {getFile, getResource} from "./common"
import {Cents, Direction} from "./transaction"
import {IncomingRealTimePaymentStatus} from "./incomingRealTimePayment"

export enum ReceivedPaymentStatus {
	Pending = "Pending",
	Completed = "Completed",
	Advanced = "Advanced",
	Returned = "Returned",
	MarkedForReturn = "MarkedForReturn",
	PendingReview = "PendingReview",
}

export enum ReceivedPaymentTypes {
	ACH = "achReceivedPayment",
	Wire = "wireReceivedPayment",
	RTP = "realtimeReceivedPayment",
}

export type ReceivedPaymentListStatus = ReceivedPaymentStatus | IncomingRealTimePaymentStatus

export enum ReceivedPaymentTypeFilters {
	ACH = "ACH",
	Wire = "Wire",
	RTP = "Realtime",
}

export type ReceivedPayment = AchReceivedPayment | WireReceivedPayment | RealtimeReceivedPayment

export type AchReceivedPayment = {
	id: string
	type: ReceivedPaymentTypes.ACH
	attributes: {
		createdAt: Date
		status: ReceivedPaymentStatus
		direction: Direction
		wasAdvanced: boolean
		completionDate?: Date
		returnReason: string
		achReturnReason: string
		amount: Cents
		description: string
		addenda: string
		companyName: string
		counterpartyRoutingNumber: string
		traceNumber: string
		secCode: string
		receivingEntityName?: string
		originatorEntityId?: string
		tags?: {[key: string]: string}
	}
	relationships: {
		account: {
			data: {
				type: "account"
				id: number
			}
		}
		customer?: {
			data: {
				type: "customer"
				id: number
			}
		}
		receivePaymentTransaction?: {
			data: {
				type: "transaction"
				id: number
			}
		}
		paymentAdvanceTransaction?: {
			data: {
				type: string
				id: number
			}
		}
		repayPaymentAdvanceTransaction?: {
			data: {
				type: "transaction"
				id: number
			}
		}
		org?: {
			data: {
				type: "transaction"
				id: number
			}
		}
		incomingAchItem?: {
			data: {
				type: "achItem"
				id: number
			}
		}
	}
}

export type WireReceivedPayment = {
	id: string
	type: ReceivedPaymentTypes.Wire
	attributes: {
		createdAt: Date
		status: ReceivedPaymentStatus
		direction: Direction
		completionDate?: Date
		returnReason?: string
		wireReturnReason?: string
		amount: Cents
		description: string
		companyName: string
		counterpartyRoutingNumber: string
		receivingEntityName?: string
		originatorEntityId?: string
		tags?: {[key: string]: string}
	}
	relationships: {
		account: {
			data: {
				type: "account"
				id: number
			}
		}
		customer?: {
			data: {
				type: "customer"
				id: number
			}
		}
		receivePaymentTransaction?: {
			data: {
				type: "transaction"
				id: number
			}
		}
		org?: {
			data: {
				type: "org"
				id: number
			}
		}
		incomingWire?: {
			data: {
				type: "incomingWire"
				id: number
			}
		}
		approvals?: {
			data: {
				id: string[]
			}
		}
	}
}

export type RealtimeReceivedPayment = {
	id: string
	type: ReceivedPaymentTypes.RTP
	attributes: {
		createdAt: Date
		returnReason: string
		rejectReason?: string
		description: string
		direction: Direction
		amount: Cents
		status: IncomingRealTimePaymentStatus
		counterparty?: {
			accountNumber: string
			name: string
			routingNumber: string
		}
	}
	relationships: {
		account?: {
			data: {
				type: string
				id: string | null
			}
		}
		customer?: {
			data: {
				type: string
				id: string | null
			}
		}
		org?: {
			data: {
				type: string
				id: string | null
			}
		}
		receivePaymentTransaction?: {
			data: {
				type: string
				id: string | null
			}
		}
	}
}

export async function getReceivedPayment(accessToken: string, id: string, include?: string) {
	return await getResource<ReceivedPayment>(`received-payments/${id}`, accessToken, {include})
}

export async function findReceivedPayments(
	accessToken: string,
	offset: number,
	sort: string,
	searchQuery: string,
	requestType: "json" | "csv",
	limit?: number | null,
	statusFilter?: ReceivedPaymentListStatus[],
	orgs?: string[],
	typeFilter?: string[],
	since?: string,
	until?: string,
	accountId?: string,
	customerId?: string,
	fromAmount?: Cents | "",
	toAmount?: Cents | "",
	include?: string
) {
	const orgFilter = orgs && orgs.length > 0 ? orgs : undefined
	const query = {
		page: {
			limit: limit,
			offset,
		},
		filter: {
			status: statusFilter,
			query: searchQuery != "" ? searchQuery : null,
			orgs: orgFilter,
			type: typeFilter,
			since: since != "" ? since : null,
			until: until != "" ? until : null,
			accountId: accountId,
			customerId: customerId,
			fromAmount: fromAmount != "" ? fromAmount : null,
			toAmount: toAmount != "" ? toAmount : null,
			isDashboard: true,
		},
		sort: sort,
		include: include ? include : "customer,org",
	}

	switch (requestType) {
		case "csv":
			return await getFile("received-payments", accessToken, query)
		default:
			return await getResource<Array<ReceivedPayment>>("received-payments", accessToken, query)
	}
}

export function isWireReceivedPayment(payment: ReceivedPayment): payment is WireReceivedPayment {
	return payment.type === ReceivedPaymentTypes.Wire
}

export function getWireReceivedPaymentCreditorName(
	payment?: WireReceivedPayment,
	override?: string
): string | undefined {
	return override || payment?.attributes?.receivingEntityName
}
